import { captureException } from '@sentry/browser'
import { routes } from 'helpers/routes'
import * as apis from 'libs/apis/helper'

import { BodyProfileType } from 'features/Profile'

import {
  setSessionStorage,
  removeSessionStorage,
  getSessionStorage,
} from 'helpers/common'
import * as AuthService from 'features/Auth/service'
import * as amplitude from '@amplitude/analytics-browser'

import * as redirect from 'helpers/redirect'
import axios from 'axios'
import * as registerNotificationTokenModule from './registerNotificationToken'
import { createEventProperties } from './amplitudeTracker'
import { getOAuthAuthorizationCode, getOAuthCallbackState } from './oauthQuery'

export const AUTH_FINISHED_STORAGE_KEY = 'isAuthenticated'

export { getOAuthAuthorizationCode, getOAuthCallbackState }

type ExchangeStep =
  | 'oauth-state'
  | 'exchange-token'
  | 'profile'
  | 'fcm'
  | 'unknown'

let inFlight: Promise<void> | null = null

export function markAuthenticationFinished(): void {
  setSessionStorage(AUTH_FINISHED_STORAGE_KEY, 'true')
}

export function clearAuthenticationFinished(): void {
  removeSessionStorage(AUTH_FINISHED_STORAGE_KEY)
}

export function hasFinishedAuthentication(
  search: string = window.location.search,
): boolean {
  if (getSessionStorage(AUTH_FINISHED_STORAGE_KEY) === 'true') {
    return true
  }

  if (getOAuthAuthorizationCode(search)) {
    return false
  }

  return Boolean(getSessionStorage('consents'))
}

function getPostLoginPath(): string {
  const pathSaved = getSessionStorage('pathRedirect')

  if (
    !pathSaved ||
    pathSaved === '/' ||
    pathSaved === '/login' ||
    pathSaved === routes.login.path
  ) {
    return routes.ticket.path
  }

  return pathSaved
}

function isValidOAuthState(search: string = window.location.search): boolean {
  const incoming = getOAuthCallbackState(search)
  const stored = getSessionStorage(redirect.OAUTH_STATE_STORAGE_KEY)

  return Boolean(incoming && stored && incoming === stored)
}

function logExchangeTokenFailure(
  error: unknown,
  step: ExchangeStep,
  exchangeSucceeded: boolean,
): void {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined
  const apiCode = axios.isAxiosError(error)
    ? error.response?.data?.code
    : undefined
  const message = error instanceof Error ? error.message : String(error)

  console.error('[exchangeToken] failed', {
    step,
    status,
    code: apiCode,
    message,
    exchangeSucceeded,
  })
  captureException(error)
}

const runExchangeToken = async (): Promise<void> => {
  const {
    REACT_APP_IAM_REDIRECT_URL: REDIRECT_URL,
    REACT_APP_API_URL,
    REACT_APP_IAM_CLIENT_ID,
  } = window._env_

  const authCode = getOAuthAuthorizationCode()

  if (!authCode) {
    redirect.redirectToSSOLogin()

    return
  }

  if (!isValidOAuthState()) {
    const error = new Error('Invalid OAuth state')
    logExchangeTokenFailure(error, 'oauth-state', false)
    removeSessionStorage(redirect.OAUTH_STATE_STORAGE_KEY)
    redirect.redirectToSSOLogin()

    return
  }

  removeSessionStorage(redirect.OAUTH_STATE_STORAGE_KEY)
  clearAuthenticationFinished()

  let step: ExchangeStep = 'exchange-token'

  try {
    await apis.fetchAPI({
      apiUrl: REACT_APP_API_URL,
      method: 'POST',
      path: `/v1/auth/exchange-token`,
      options: {
        data: {
          redirect_uri: REDIRECT_URL,
          code: authCode,
          grant_type: 'authorization_code',
          client_id: REACT_APP_IAM_CLIENT_ID,
        },
      },
    })

    markAuthenticationFinished()

    step = 'profile'
    const { body: bodyProfile } = await apis.fetchAPI<BodyProfileType>({
      apiUrl: window._env_.REACT_APP_API_URL,
      method: 'GET',
      path: `/v1/user/profile`,
    })

    const bofConsent = bodyProfile?.consents?.find(
      (profile) => profile.applicationId === 'fixtab-backoffice',
    )

    step = 'fcm'
    await registerNotificationTokenModule.registerNotificationToken()
    const eventProperties = createEventProperties()
    amplitude.track('FixTab Backoffice: Login', eventProperties)

    removeSessionStorage('isMenuOpen')
    removeSessionStorage('consents')

    if (bofConsent) {
      setSessionStorage('consents', 'fixtab-backoffice')
      window.location.replace(getPostLoginPath())
    } else {
      window.location.replace(routes.pdpa.path)
    }
  } catch (error: unknown) {
    const exchangeSucceeded =
      getSessionStorage(AUTH_FINISHED_STORAGE_KEY) === 'true'

    logExchangeTokenFailure(error, step, exchangeSucceeded)

    if (exchangeSucceeded) {
      window.location.replace(routes.error[500])

      return
    }

    clearAuthenticationFinished()

    const status = axios.isAxiosError(error)
      ? error.response?.status
      : undefined

    if (status === 401) {
      await AuthService.logout()

      return
    }

    window.location.replace(routes.error[500])
  }
}

export const exchangeToken = (): Promise<void> => {
  if (inFlight) {
    return inFlight
  }

  inFlight = runExchangeToken().finally(() => {
    inFlight = null
  })

  return inFlight
}
